// hooks/useShipmentSort.ts
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type SortOrder = 'ascending' | 'descending';

export const sortOptions = [
  { key: 'scheduled_delivery_date', label: 'Delivery date' },
  { key: 'scheduled_pickup_date', label: 'Pickup date' },
  { key: 'created_at', label: 'Created date' },
  {
    key: 'last_status_update',
    label: 'Last updated',
  },
  {
    key: 'courier_name',
    label: `${themeT('transportProvider', theme.themeType)}`,
  },
  { key: 'team', label: 'Primary team' },
  { key: 'alert_severity', label: 'Alert severity' },
  { key: 'shipment_status', label: 'Shipment status' },
  { key: 'alert_count', label: '# Alerts' },
  { key: 'alert_age', label: 'Alert age' },
];

interface UseShipmentSortParams {
  tabId: string;
  initialSortKey?: string;
  initialSortOrder?: SortOrder;
  onUserSortChange?: (sort: { sortKey: string; sortOrder: SortOrder }) => void;
}

export const useShipmentSort = ({
  tabId,
  initialSortKey = 'scheduled_delivery_date',
  initialSortOrder = 'ascending',
  onUserSortChange,
}: UseShipmentSortParams) => {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const lastTabIdRef = useRef(tabId);
  const lastInitialSortRef = useRef({
    tabId,
    sortKey: initialSortKey,
    sortOrder: initialSortOrder,
  });
  const hasUserChangedSortRef = useRef(false);

  useEffect(() => {
    const didTabChange = lastTabIdRef.current !== tabId;
    const didInitialSortChange =
      lastInitialSortRef.current.tabId !== tabId ||
      lastInitialSortRef.current.sortKey !== initialSortKey ||
      lastInitialSortRef.current.sortOrder !== initialSortOrder;

    lastInitialSortRef.current = {
      tabId,
      sortKey: initialSortKey,
      sortOrder: initialSortOrder,
    };

    if (
      !didTabChange &&
      (!didInitialSortChange || hasUserChangedSortRef.current)
    ) {
      return;
    }

    lastTabIdRef.current = tabId;
    hasUserChangedSortRef.current = false;
    setSortKey(initialSortKey);
    setSortOrder(initialSortOrder);
  }, [initialSortKey, initialSortOrder, tabId]);

  const selectedSortLabel = useMemo(
    () => sortOptions.find((option) => option.key === sortKey)?.label ?? '',
    [sortKey]
  );

  const handleSortChange = useCallback(
    (key: string) => {
      if (key === sortKey) return;

      hasUserChangedSortRef.current = true;
      // Alert severity defaults to most-severe-first (descending).
      const nextSortOrder: SortOrder =
        key === 'alert_severity' ? 'descending' : sortOrder;
      setSortKey(key);
      setSortOrder(nextSortOrder);
      onUserSortChange?.({ sortKey: key, sortOrder: nextSortOrder });
    },
    [onUserSortChange, sortKey, sortOrder]
  );

  const handleSortOrderToggle = useCallback(() => {
    setSortOrder((prev) => {
      const nextSortOrder = prev === 'ascending' ? 'descending' : 'ascending';
      hasUserChangedSortRef.current = true;
      onUserSortChange?.({ sortKey, sortOrder: nextSortOrder });
      return nextSortOrder;
    });
  }, [onUserSortChange, sortKey]);

  const sortMenu = useMemo(
    () => ({
      items: sortOptions.map((option) => ({
        key: option.key,
        label: option.label,
        onClick: () => handleSortChange(option.key),
      })),
    }),
    [handleSortChange]
  );

  return {
    sortKey,
    sortOrder,
    sortOptions,
    sortMenu,
    selectedSortLabel,
    handleSortChange,
    handleSortOrderToggle,
  };
};
